import React, { FunctionComponent, useEffect, useMemo } from 'react';
import { useTranslation } from '@rbx/intl';
import {
  TCategoryClearedInteraction,
  TCategoryClickedInteraction,
} from '@modules/search/searchEvents';
import { SEARCH_CATEGORIES, SearchCategory } from './utils/searchCategories';
import { SearchList } from './SearchList';
import { TSearchListItem } from './types/SearchListItem';
import { SearchListItemLinkProps } from './SearchListItem';
import useRecommendedSearch from './recommendations/hooks/useRecommendedSearch';

interface SearchContentProps {
  isFilterDefault: boolean;
  searchListItems: Record<string, TSearchListItem[]>;
  onClickItem: SearchListItemLinkProps['onClickItem'];
  onClickFilter?: (filter: SearchCategory, interaction: TCategoryClickedInteraction) => void;
  onClearFilter?: (filter: SearchCategory, interaction: TCategoryClearedInteraction) => void;
}

const SearchContentV2: FunctionComponent<React.PropsWithChildren<SearchContentProps>> = ({
  isFilterDefault,
  searchListItems,
  onClickItem,
  onClickFilter,
  onClearFilter,
}) => {
  const { setExclusions: setDoNotRecommendArray } = useRecommendedSearch();
  const { translate } = useTranslation();

  // Update recommendations when search results change
  useEffect(() => {
    const allResults = Object.values(searchListItems).flat();
    setDoNotRecommendArray(allResults.map((item) => item.title));
  }, [searchListItems, setDoNotRecommendArray]);

  // Memoize the content types to avoid recalculating on every render
  const contentTypes = useMemo(() => Object.keys(searchListItems), [searchListItems]);

  // Memoize the category mapping to avoid repeated find operations
  const categoryMap = useMemo(() => {
    return contentTypes.reduce(
      (acc, contentType) => {
        const category = SEARCH_CATEGORIES.find((cat) => cat.value === contentType);
        if (category) {
          acc[contentType] = category;
        }
        return acc;
      },
      {} as Record<string, SearchCategory>,
    );
  }, [contentTypes]);

  return (
    <React.Fragment>
      {contentTypes.map((contentType) => {
        const items = searchListItems[contentType];
        const category = categoryMap[contentType];
        return category && items ? (
          <SearchList
            key={contentType}
            isTitleClickable
            items={items}
            title={translate(category.translationKey)}
            onClickItem={onClickItem}
            onClickTitle={(interaction) => onClickFilter?.(category, interaction)}
            onClearFilter={(interaction) => onClearFilter?.(category, interaction)}
            showAllResultsButton={!isFilterDefault}
          />
        ) : null;
      })}
    </React.Fragment>
  );
};

export default SearchContentV2;
